import { useMemo } from 'react';
import { useAuthStore } from '../stores/useAuthStore';
import { useCompanyStore } from '../stores/useCompanyStore';

/** Resolved role string (uppercase), including per-empresa roles not in tipo_usuario. */
export type EmpresaRole =
  | 'OWNER'
  | 'ADMIN'
  | 'GERENTE'
  | 'CONTADOR'
  | 'EMPLEADO'
  | 'VENDEDOR'
  | 'DOMICILIARIO'
  | 'SOLO_LECTURA'
  | null;

export function normalizeEmpresaRole(raw: string | null | undefined): EmpresaRole {
  if (!raw) return null;
  return String(raw).toUpperCase().trim() as EmpresaRole;
}

const CAN_SEE_COSTO = new Set<string>(['OWNER', 'ADMIN', 'GERENTE', 'CONTADOR']);
const CAN_EDIT_PRECIO = new Set<string>(['OWNER', 'ADMIN', 'GERENTE']);
const CAN_EDIT_PRODUCTO = new Set<string>(['OWNER', 'ADMIN', 'GERENTE']);
const CAN_VER_REPORTES = new Set<string>(['OWNER', 'ADMIN', 'GERENTE', 'CONTADOR']);
const IS_ADMIN = new Set<string>(['OWNER', 'ADMIN', 'GERENTE']);

export interface EmpresaRolePermissions {
  role: EmpresaRole;
  canSeeCosto: boolean;
  canEditPrecio: boolean;
  canEditProducto: boolean;
  canVerReportes: boolean;
  isAdmin: boolean;
}

/**
 * Product/cost/report gates from usuarios_empresa.role (or OWNER) + tipo_usuario fallback.
 * Distinct from RBAC hook usePermissions(userId, empresaId) in ./usePermissions.ts
 */
export function useEmpresaRolePermissions(): EmpresaRolePermissions {
  const usuario = useAuthStore((s) => s.usuario);
  const userEmpresas = useAuthStore((s) => s.userEmpresas);
  const authEmpresa = useAuthStore((s) => s.currentEmpresa);
  const companyEmpresa = useCompanyStore((s) => s.currentEmpresa);
  const currentEmpresa = companyEmpresa || authEmpresa;

  const role = useMemo((): EmpresaRole => {
    const entry = userEmpresas.find((ue) => ue.empresa_id === currentEmpresa?.id);
    const ownerMatch =
      currentEmpresa && usuario?.id && (currentEmpresa as { owner_id?: string }).owner_id === usuario.id;
    const raw =
      entry?.role
      || (ownerMatch ? 'OWNER' : null)
      || usuario?.tipo_usuario
      || usuario?.tipoUsuario
      || null;
    return normalizeEmpresaRole(raw);
  }, [usuario, currentEmpresa, userEmpresas]);

  return useMemo(() => {
    const key = role ?? '';
    return {
      role,
      canSeeCosto: CAN_SEE_COSTO.has(key),
      canEditPrecio: CAN_EDIT_PRECIO.has(key),
      canEditProducto: CAN_EDIT_PRODUCTO.has(key),
      canVerReportes: CAN_VER_REPORTES.has(key),
      isAdmin: IS_ADMIN.has(key),
    };
  }, [role]);
}

export function getEmpresaRolePermissions(rawRole: string | null | undefined): EmpresaRolePermissions {
  const role = normalizeEmpresaRole(rawRole);
  const key = role ?? '';
  return {
    role,
    canSeeCosto: CAN_SEE_COSTO.has(key),
    canEditPrecio: CAN_EDIT_PRECIO.has(key),
    canEditProducto: CAN_EDIT_PRODUCTO.has(key),
    canVerReportes: CAN_VER_REPORTES.has(key),
    isAdmin: IS_ADMIN.has(key),
  };
}
